import React, { useMemo } from "react";
import BaseTable from "@components/DataTable/BaseTable";
import type { Header } from "@components/DataTable/CustomDataTable.types";
import Paper from "@components/Paper/Paper";
import { sortPortCallsByClosestDates } from "@features/vessel-call/utils/sortPortCallsByClosestDates.ts";
import {
  formatDate,
  LAST_PORTS_FIELDS,
} from "@features/vessel-call/vessel-call-status/VesselCallDetails/lastPortsUtils.ts";
import type { VesselCallPortCall } from "@features/vessel-call/vessel-call-status/VesselCallStatus.types.tsx";
import { useFormattedReference } from "@hooks/useFormattedReference";
import { REFERENCE_DATA_TYPES } from "@services/referenceDataApi";
import type { Port } from "@services/vessel/types";
import { useTranslation } from "react-i18next";

export type PortCallRow = {
  id: number;
  port: string;
  country: string;
  arrivalDate: string;
  departureDate: string;
  securityLevel: string;
  comments: string;
};

interface LastPortsTableProps {
  rows?: VesselCallPortCall[];
}

const LastPortsTable: React.FC<LastPortsTableProps> = ({ rows }) => {
  const { t } = useTranslation();
  const { options: ports = [] } = useFormattedReference<Port>(
    REFERENCE_DATA_TYPES.PORTS,
    "bff-vessel",
  );
  const { options: countries = [] } = useFormattedReference<{
    countryCode: string;
    countryName?: string;
  }>(REFERENCE_DATA_TYPES.COUNTRIES, "bff-vessel");

  const portNameByCode = useMemo(() => {
    const map = new Map<string, string>();
    for (const port of ports) {
      if (port?.portCode && port?.portName) {
        map.set(port.portCode, `${port.portName} (${port.portCode})`);
      }
    }
    return map;
  }, [ports]);

  const countryNameByCode = useMemo(() => {
    const map = new Map<string, string>();
    for (const country of countries) {
      if (country?.countryCode && country?.countryName) {
        map.set(country.countryCode, country.countryName);
      }
    }
    return map;
  }, [countries]);

  const headers: Header[] = useMemo(
    () => [
      {
        field: LAST_PORTS_FIELDS.PORT,
        columnName: t("vessel-call.last-ten-ports.grid-headers.port"),
      },
      {
        field: LAST_PORTS_FIELDS.COUNTRY,
        columnName: t("vessel-call.last-ten-ports.grid-headers.country"),
      },
      {
        field: LAST_PORTS_FIELDS.ARRIVAL_DATE,
        columnName: t("vessel-call.last-ten-ports.grid-headers.arrival-date"),
      },
      {
        field: LAST_PORTS_FIELDS.DEPARTURE_DATE,
        columnName: t("vessel-call.last-ten-ports.grid-headers.departure-date"),
      },
      {
        field: LAST_PORTS_FIELDS.SECURITY_LEVEL,
        columnName: t("vessel-call.last-ten-ports.grid-headers.security-level-of-port-call"),
      },
      {
        field: LAST_PORTS_FIELDS.COMMENTS,
        columnName: t("vessel-call.last-ten-ports.grid-headers.comments"),
      },
    ],
    [t],
  );

  const mappedRows: PortCallRow[] = sortPortCallsByClosestDates(rows ?? []).map((p, id) => {
    const portLabel = portNameByCode.get(p.port ?? "") ?? p.port;
    const countryLabel = countryNameByCode.get(p.country ?? "") ?? p.country;

    return {
      id,
      [LAST_PORTS_FIELDS.PORT]: portLabel || "-",
      [LAST_PORTS_FIELDS.COUNTRY]: countryLabel || "-",
      [LAST_PORTS_FIELDS.ARRIVAL_DATE]: formatDate(p.arrivalDate),
      [LAST_PORTS_FIELDS.DEPARTURE_DATE]: formatDate(p.departureDate),
      [LAST_PORTS_FIELDS.SECURITY_LEVEL]: p.securityLevel || "-",
      [LAST_PORTS_FIELDS.COMMENTS]: p.internalNotes || "-",
    };
  });

  const tableTitle = t("vessel-call.last-ten-ports.title");

  return (
    <Paper variant="outlined">
      <p className="font-bold mb-4">{tableTitle}</p>
      <BaseTable rows={mappedRows} headers={headers} />
    </Paper>
  );
};

export default LastPortsTable;
